<script>
import vSelect from "vue-select"
import TextField from "@/components/global/TextField"
import "vue-select/dist/vue-select.css"

export default {
  name: "RelativePicker",
  components: {
    vSelect,
    TextField,
  },
  props: {
    value:        { type: Object, required: true },
    forceLabel:   { type: Boolean, default: false },
  },
  data() {
    return {
      debug: false,
      options: [],
      selected: {text: "", value: ""},
      key: 0,
      observer: null,
      limit: 10,
      search: "",
    }
  },
  computed: {
    filtered () {
      return this.options.filter(itm => itm.text ? itm.text.toLowerCase().includes(this.search.toLowerCase()) : false)
    },
    paginated () {
      return this.filtered.slice(0, this.limit)
    },
    hasNextPage () {
      return this.paginated.length < this.filtered.length
    },
    readOnly () {
      return this.value.readOnly !== null ? this.value.readOnly : false
    },
    defaultLabel () {
      return this.value.defaultLabel !== null ? this.value.defaultLabel : ""
    },
    readOnlyData () {
      return {
        id: this.value.id,
        label: this.value.label,
        value: this.selected.text,
        readOnly: true,
      }
    },
  },
  watch: {
    value: {
      handler(data) {
        if (this.debug) console.log("[RelativePicker.vue.vue] (watch)value:", data) 

        this.$emit("input", data)
        if (this.siteCode !== data.siteCode) {
          this.siteCode = data.siteCode
          this.retrieveData()
        } else {
          this.updateSelected(data.value)
        }
      },
      deep: true,
    },
    selected(sel) {
      if (this.debug) console.log("[RelativePicker.vue.vue] (watch)selected:", sel)
      this.value.value = sel !== null ? sel.value : ""
    },
  },
  created() {
    if (this.debug) console.log("[RelativePicker.vue.vue] created", this.value)
    this.retrieveData()
    this.observer = new IntersectionObserver(this.infiniteScroll)
  },
  methods: {
    updateSelected(newVal) {
      if (this.debug) console.log("[RelativePicker.vue.vue] updateSelected:", newVal) 
      if (newVal === null) newVal = ""
      for (let i=0;i<this.options.length;i++) {
        const cur = this.options[i]
        if (cur.value == newVal) {
          this.selected = {
            text: cur.text,
            value: cur.value,
          }
          this.$emit("change", this.selected)
          break
        }
      }
    },
    retrieveData() {
      if (this.debug) console.log("[RelativePicker.vue.vue] retrieveData") 

      const options = [{ value: "", text: this.value.defaultLabel }]
      let selected = options[0]

      // Add Months:
      for (let i=1; i < 13; i++) {
        const text = `${i} Month${i > 1 ? "s" : ""} ago`
        const value = `${i} months`
        options.push({
          text,
          value,
        })
        if (this.value.value == value) {
          selected = {
            text,
            value,
          }
        }
      }

      // Add Days:
      for (let i=1; i < 31; i++) {
        const text = `${i} Day${i > 1 ? "s" : ""} ago`
        const value = `${i} days`
        options.push({
          text,
          value,
        })
        if (this.value.value == value) {
          selected = {
            text,
            value,
          }
        }
      }

      this.selected = selected
      this.options = options
      this.key++
    },
    async onOpen () {
      if (this.hasNextPage) {
        await this.$nextTick()
        this.observer.observe(this.$refs.load)
      }
    },
    onClose () {
      this.observer.disconnect()
    },
    async infiniteScroll ([{isIntersecting, target}]) {
      if (isIntersecting) {
        const ul = target.offsetParent
        const scrollTop = target.offsetParent.scrollTop
        this.limit += 10
        await this.$nextTick()
        ul.scrollTop = scrollTop
      }
    },
  },
}
</script>

<template>
  <div class="relativePicker">
    <label
      v-if="forceLabel"
      :for="value.id"
    >
      {{ value.label }}:
    </label>
    <b-form-group
      v-if="!readOnly"
      :id="'input_' + value.id"
      :description="value.description"
    >
      <v-select 
        v-model="selected"
        :id="value.id"
        :key="key"
        label="text"
        :options="paginated"
        :filterable="false"
        :clearable="false"
        :disabled="readOnly"
        :single-line="true"
        @open="onOpen"
        @close="onClose"
        @search="query => search = query"
      >
        <template #list-footer>
          <li v-show="hasNextPage" ref="load" class="loader">
            Loading more options...
          </li>
        </template>
      </v-select>
    </b-form-group>
    <TextField v-if="readOnly" :value="readOnlyData" />
  </div>
</template>

<style>
  .relativePicker.vue div.vs__dropdown-toggle {
    background: white;
    white-space: nowrap;
  }
  .relativePicker.vue div.vs__dropdown-toggle .vs__selected {
    position: absolute;
  }
  .relativePicker.vue div.vs__dropdown-toggle .vs__selected-options {
    overflow: hidden;
  }
</style>
